import React, {Component, ErrorInfo, ReactNode} from "react";
import {Box, Button, Stack, Typography} from "@mui/material";

// Единственный механизм репортинга в проекте сейчас — console.error
// (отправки ошибок на бэк нет). componentDidCatch только логирует;
// если появится реальный канал отправки — добавлять здесь, в reportError.
const reportError = (error: Error, info: ErrorInfo) => {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] перехвачена ошибка рендера:", error, info.componentStack);
};

const isDev = process.env.NODE_ENV !== "production";

interface ErrorBoundaryProps {
    children: ReactNode;
    // Заголовок fallback-сообщения
    title?: string;
    // Поясняющий текст
    description?: string;
    // compact — для изолированной зоны (карта, секция): рендер без кнопки
    // «На главную», более скромная вёрстка, остальной layout остаётся жив.
    compact?: boolean;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
    info: ErrorInfo | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    state: ErrorBoundaryState = {hasError: false, error: null, info: null};

    static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
        return {hasError: true, error};
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        this.setState({info});
        reportError(error, info);
    }

    private handleReload = () => {
        window.location.reload();
    };

    private handleHome = () => {
        window.location.assign("/");
    };

    render() {
        if (!this.state.hasError) {
            return this.props.children;
        }

        const {compact, title, description} = this.props;
        const {error, info} = this.state;

        return (
            <Box
                role="alert"
                sx={{
                    flex: 1,
                    minHeight: compact ? 240 : "60vh",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                    gap: 2,
                    p: 3,
                }}
            >
                <Typography variant={compact ? "h6" : "h5"} sx={{fontWeight: 600}}>
                    {title ?? "Что-то пошло не так"}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{maxWidth: 480}}>
                    {description ??
                        "Произошла непредвиденная ошибка. Попробуйте перезагрузить страницу — если повторится, сообщите в поддержку."}
                </Typography>

                <Stack direction="row" spacing={2} sx={{mt: 1}}>
                    <Button variant="contained" onClick={this.handleReload}>
                        Перезагрузить страницу
                    </Button>
                    {!compact && (
                        <Button variant="outlined" onClick={this.handleHome}>
                            На главную
                        </Button>
                    )}
                </Stack>

                {isDev && error && (
                    <Box
                        component="pre"
                        sx={{
                            mt: 2,
                            maxWidth: "100%",
                            maxHeight: 320,
                            overflow: "auto",
                            textAlign: "left",
                            fontSize: 12,
                            color: "error.main",
                            bgcolor: "action.hover",
                            borderRadius: 1,
                            p: 2,
                            whiteSpace: "pre-wrap",
                        }}
                    >
                        {error.message}
                        {"\n"}
                        {error.stack}
                        {info?.componentStack}
                    </Box>
                )}
            </Box>
        );
    }
}

export default ErrorBoundary;
